/**
 * Equipment Helper Functions
 * Utilities for looking up and managing equipment data (armor, weapons, resources)
 */

/**
 * Get armor information from equipment database or campaign frames
 * @param {string|object} armorId - Armor ID or armor object
 * @param {object} allData - Game data context
 * @param {object} character - Character data (for campaign frame lookup)
 * @returns {object|null} Armor data or null if not found
 */
export const getArmorInfo = (armorId, allData, character) => {
  if (!armorId) return null;

  // If armorId is already an object, return it directly
  if (typeof armorId === 'object' && armorId.id) {
    return armorId;
  }

  // Check equipment database (now flattened structure)
  // Note: Keys may differ from IDs (e.g., key "chainmail_armor" vs id "chainmail_armor_t1")
  if (allData.equipmentDatabase && allData.equipmentDatabase.armor) {
    for (const itemKey in allData.equipmentDatabase.armor) {
      const armor = allData.equipmentDatabase.armor[itemKey];
      if (armor && (armor.id === armorId || itemKey === armorId)) {
        return armor;
      }
    }
  }

  // Fallback: check campaign frame armor (stored as array)
  if (character.campaignFrame !== 'core' && allData.campaignFrames && allData.campaignFrames[character.campaignFrame]) {
    const frameData = allData.campaignFrames[character.campaignFrame];
    if (frameData.startingEquipment && frameData.startingEquipment.armor) {
      // armor is an array, so find by id
      return frameData.startingEquipment.armor.find(armor => armor.id === armorId);
    }
  }

  return null;
};

/**
 * Get weapon information from equipment database or campaign frames
 * @param {string|object} weaponId - Weapon ID or weapon object
 * @param {object} allData - Game data context
 * @param {object} character - Character data (for campaign frame lookup)
 * @returns {object|null} Weapon data or null if not found
 */
export const getWeaponInfo = (weaponId, allData, character) => {
  if (!weaponId) return null;

  // If weaponId is already an object, return it directly
  if (typeof weaponId === 'object' && weaponId.id) {
    return weaponId;
  }

  // Check equipment database (now flattened structure)
  // Note: Keys may differ from IDs (e.g., key "longsword" vs id "longsword_t1")
  if (allData.equipmentDatabase) {
    const { equipmentDatabase } = allData;

    // Search primary weapons (flattened structure: equipmentDatabase.primaryWeapons[itemKey])
    if (equipmentDatabase.primaryWeapons) {
      for (const itemKey in equipmentDatabase.primaryWeapons) {
        const weapon = equipmentDatabase.primaryWeapons[itemKey];
        if (weapon && (weapon.id === weaponId || itemKey === weaponId)) {
          return weapon;
        }
      }
    }

    // Search secondary weapons (flattened structure: equipmentDatabase.secondaryWeapons[itemKey])
    if (equipmentDatabase.secondaryWeapons) {
      for (const itemKey in equipmentDatabase.secondaryWeapons) {
        const weapon = equipmentDatabase.secondaryWeapons[itemKey];
        if (weapon && (weapon.id === weaponId || itemKey === weaponId)) {
          return weapon;
        }
      }
    }
  }

  // Fallback: check campaign frame weapons
  if (character.campaignFrame !== 'core' && allData.campaignFrames && allData.campaignFrames[character.campaignFrame]) {
    const frameData = allData.campaignFrames[character.campaignFrame];
    if (frameData.startingEquipment) {
      // Check primary weapons
      const primaryWeapon = frameData.startingEquipment.primaryWeapons?.find(w => w.id === weaponId);
      if (primaryWeapon) return primaryWeapon;

      // Check secondary weapons
      const secondaryWeapon = frameData.startingEquipment.secondaryWeapons?.find(w => w.id === weaponId);
      if (secondaryWeapon) return secondaryWeapon;
    }
  }

  return null;
};

/**
 * Get weapon-specific character resources
 * @param {object} weaponInfo - Weapon data
 * @param {object} character - Character data with resources
 * @returns {Array} Array of resource objects related to the weapon
 */
export const getWeaponResources = (weaponInfo, character) => {
  if (!weaponInfo || !character.resources) return [];

  const weaponResources = [];
  const weaponName = weaponInfo.name?.toLowerCase() || '';

  // Look for resources that might be related to this weapon
  Object.keys(character.resources).forEach(resourceKey => {
    const resource = character.resources[resourceKey];
    const resourceName = resource.name?.toLowerCase() || '';

    // Check if this resource might be related to the weapon
    if (resourceName.includes(weaponName) ||
        resourceName.includes('weapon') ||
        resourceName.includes('attack') ||
        (resource.type === 'usage' && resourceName.includes('ability'))) {
      weaponResources.push({
        id: resourceKey,
        ...resource
      });
    }
  });

  return weaponResources;
};
